import { useState } from 'react';
import { X, Share2, Copy, Check } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';

interface InviteModalProps {
  token: string;
  onClose: () => void;
}

export function InviteModal({ token, onClose }: InviteModalProps) {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  
  const joinUrl = `https://tugedr.com/join?token=${token}`;
  
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: '💯 Drinks in 365 Days',
          text: `Join my 365-day challenge on 💯 Drinks\nInvite code: ${token}`,
          url: joinUrl,
        });
      } catch (error) {
        // User cancelled or share failed - silently ignore
        if ((error as Error).name !== 'AbortError') {
          handleCopyLink();
        }
      }
    } else {
      handleCopyLink();
    }
  };
  
  const handleCopyLink = () => {
    navigator.clipboard.writeText(joinUrl);
    setCopied(true);
    toast.success(t('inviteModalCopied'));
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className="fixed inset-0 z-[60] bg-background flex flex-col items-center justify-center p-6">
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full hover:bg-muted transition-colors"
        aria-label="Close"
      >
        <X className="h-8 w-8 text-foreground" />
      </button>
      
      <div className="w-full max-w-sm flex flex-col items-center space-y-8">
        {/* Headline */}
        <h1 className="text-2xl font-serif font-semibold text-center text-foreground">
          {t('inviteModalHeadline')}
        </h1>
        
        {/* Invite code display */}
        <div className="text-center">
          <div className="text-5xl font-mono font-bold tracking-[0.3em] text-primary">
            {token}
          </div>
        </div>
        
        {/* CTAs */}
        <div className="w-full space-y-3">
          {/* Primary: Share */}
          <Button 
            onClick={handleShare} 
            className="w-full h-14 text-lg gap-3"
            size="lg"
          >
            <Share2 className="h-5 w-5" />
            {t('inviteModalShare')}
          </Button>
          
          {/* Secondary: Copy */}
          <Button 
            onClick={handleCopyLink} 
            variant="outline"
            className="w-full h-12 gap-2"
          >
            {copied ? (
              <>
                <Check className="h-4 w-4" />
                {t('inviteModalCopied')}
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" />
                {t('inviteModalCopyLink')}
              </>
            )}
          </Button>
        </div>
        
        {/* QR Code */}
        <div className="p-4 bg-white rounded-xl">
          <QRCodeSVG 
            value={joinUrl}
            size={160}
            level="M"
            includeMargin={false}
          />
        </div>
        
        {/* Helper text */}
        <p className="text-sm text-muted-foreground text-center max-w-xs">
          {t('inviteModalHelper')}
        </p>
      </div>
    </div>
  );
}
